// Comment thunks extracted from `src/modules/Services/ServicesThunks.js`.

import _ from "lodash";
import moment from "moment";
import client from "lib/apolloClient/client";
import {
  ATTACHMENT_OMIT_ATTRIBUTES,
  ATTACHMENT_CREATE_ATTRIBUTES,
} from "store/static";
import { OmitFromArrayValues, generateRandomId, getTaggedUserIds } from "Utils";
import {
  createMessageMutation,
  updateMessageMutation,
  deleteMessageMutation,
} from "modules/CommonMutations";
import {
  getCommentsOfNodeFromCache,
  writeCommentNodeFragment,
  writeMessageFragment,
  getUserBasicDetailsFromCache,
} from "modules/CommonGraphqlHelpers";
import { setToastMsg, getUserInfo } from "Login/modules/LoginModule";
import { recordEvent } from "lib/eventTracker";
import { updateLocalComment } from "modules/Services/ServicesActions";

export const createComment = ({
  nodeId,
  type,
  parentType,
  collaborators = [],
  commentsKey,
  parentMessageId,
  message,
  itemType,
  entityName,
  replyItemId,
  newCommentLabel,
  attachments,
  commentFilters,
} = {}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const isCommunity = getState().login.activeTab == "community";
    const schoolTenures = [];

    const nodeDetails = getCommentsOfNodeFromCache({
      nodeId,
      type,
      isCommunity,
      commentFilters,
    });

    const label = (itemType === "REPLY" ? message : newCommentLabel) || "";
    const isCreateEmpty = !_.trim(label) && _.isEmpty(attachments);

    // Reset local comment
    if (itemType !== "REPLY")
      dispatch(
        updateLocalComment({
          id: nodeId,
          commentData: { label: "", attachments: [] },
        })
      );

    if (isCreateEmpty) {
      return;
    }

    const variables = {
      parentId: nodeId,
      parentType: parentType,
      label: _.trim(label),
      subEntityType: "COMMENT",
    };

    if (itemType === "REPLY") {
      variables["parentMessageId"] = replyItemId
        ? replyItemId
        : parentMessageId;
    }

    const taggedUsers = getTaggedUserIds({
      comment: label,
      collaborators,
    });

    if (!_.isEmpty(taggedUsers)) {
      variables["taggedUsers"] = taggedUsers;
    }

    if (!_.isEmpty(attachments)) {
      variables["attachments"] = OmitFromArrayValues({
        arr: attachments,
        omitKeys: ["id", ...ATTACHMENT_OMIT_ATTRIBUTES],
      });
    }

    const comments = _.get(nodeDetails, `${commentsKey}.edges`, []);
    const newComment = {
      id: generateRandomId(),
      isDeleted: false,
      isCommentRead: true,
      label: label.trim(),
      itemType: "ATTACHMENTS",
      itemTypeV2: "ATTACHMENTS",
      metadata: null,
      type: "NORMAL",
      item: {
        ...ATTACHMENT_CREATE_ATTRIBUTES,
        id: generateRandomId(),
        attachments: _.map(attachments, attachment => ({
          parentType: "MESSAGE_ITEM",
          signedUrl: null,
          streamUrl: null,
          createdBy: null,
          ..._.omit(ATTACHMENT_CREATE_ATTRIBUTES, ["presetAttachment"]),
          ...attachment,
        })),
        __typename: "AttachmentSet",
      },
      isRead: false,
      isPin: false,
      createdBy: {
        ...getUserBasicDetailsFromCache({
          id: userInfo.id,
          type: userInfo.userEntityType,
        }),
        type: userInfo.user_type,
        schoolTenures,
      },
      createdAt: moment().toISOString(),
      messageReplies: {
        edges: [],
        __typename: "MessageRepliesConnection",
      },
      similarityReport: null,
      __typename: "Message",
    };
    try {
      const result = await client.mutate({
        mutation: createMessageMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            createMessage: newComment,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { createMessage },
            },
          }
        ) => {
          let updatedComments;

          if (itemType === "REPLY") {
            updatedComments = _.map(comments, comment => {
              const { node } = comment;
              if (node.id === parentMessageId) {
                const messageReplies = _.get(node, "messageReplies.edges", []);

                return {
                  __typename: "ConversationMessageEdge",
                  node: {
                    ...node,
                    messageReplies: {
                      edges: [
                        {
                          node: createMessage,
                          __typename: "MessageRepliesEdge",
                        },
                        ...messageReplies,
                      ],
                      __typename: "MessageRepliesConnection",
                    },
                  },
                };
              }
              return comment;
            });
          } else {
            updatedComments = [
              {
                node: createMessage,
                __typename: "ConversationMessageEdge",
              },
              ...comments,
            ];
          }
          const commentUsersNode = {
            ...nodeDetails[commentsKey],
            edges: updatedComments,
            totalCount: nodeDetails[commentsKey].totalCount + 1,
          };
          const data = {
            ...nodeDetails,
            [commentsKey]: commentUsersNode,
          };
          setTimeout(() => {
            writeCommentNodeFragment({
              nodeId,
              data,
              parentType,
              type,
              commentFilters,
              isCommunity,
            });
          });
        },
      });

      if (isCommunity)
        recordEvent({
          eventName: itemType === "REPLY" ? "Comment reply" : "Comment created",
          eventData: {
            source: "community",
            entity_id: nodeId,
            entity_type: type,
            entity_name: entityName,
          },
        });

      return _.get(result, "data.platform.createMessage.id");
    } catch (e) {
      //console.log(e);
      setTimeout(() => {
        writeCommentNodeFragment({
          nodeId,
          parentType,
          type,
          data: nodeDetails,
          commentFilters,
          isCommunity,
        });
      });
      // Set back the comment data to box
      dispatch(
        updateLocalComment({
          id: nodeId,
          commentData: { label, attachments },
        })
      );
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
    //await dispatch(markCommentRead({ field_id:nodeId }));
  };
};

export const updateComment = ({
  comment_id,
  comment,
  commentDetails,
  collaborators = [],
  attachments = [],
  commentsKey,
}) => {
  return async (dispatch, getState) => {
    const label = _.trim(comment);
    writeMessageFragment({
      nodeId: comment_id,
      data: {
        ...commentDetails,
        item: commentDetails.item
          ? {
              ...commentDetails.item,
              attachments,
            }
          : null,
        label,
      },
    });

    const variables = {
      id: comment_id,
      label,
    };

    const taggedUsers = getTaggedUserIds({
      comment: label,
      collaborators,
    });
    if (!_.isEmpty(taggedUsers)) {
      variables["taggedUsers"] = taggedUsers;
    }
    if (!_.isEmpty(attachments)) {
      variables["attachments"] = OmitFromArrayValues({
        arr: attachments,
        omitKeys: ["id", ...ATTACHMENT_OMIT_ATTRIBUTES],
      });
    } else {
      variables["attachments"] = [];
    }
    try {
      await client.mutate({
        mutation: updateMessageMutation,
        variables,
      });
    } catch (e) {
      writeMessageFragment({
        nodeId: comment_id,
        data: commentDetails,
      });
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      throw e;
    }
  };
};

export const deleteComment = ({
  nodeId,
  comment_id,
  type,
  parentType,
  commentsKey,
  itemType,
  parentMessageId,
  entityName,
  commentFilters,
}) => {
  return async (dispatch, getState) => {
    const isCommunity = getState().login.activeTab == "community";
    const nodeDetails = getCommentsOfNodeFromCache({
      nodeId,
      type,
      isCommunity,
      commentFilters,
    });
    const comments = nodeDetails[commentsKey].edges;
    let updatedComments;

    if (itemType === "REPLY") {
      updatedComments = _.map(comments, comment => {
        const { node } = comment;
        if (node.id === parentMessageId) {
          const messageReplies = _.get(node, "messageReplies.edges", []);
          const filteredReplies = _.filter(
            messageReplies,
            message => message.node.id !== comment_id
          );

          return {
            __typename: "ConversationMessageEdge",
            node: {
              ...node,
              messageReplies: {
                edges: filteredReplies,
                __typename: "MessageRepliesConnection",
              },
            },
          };
        }
        return comment;
      });
    } else {
      updatedComments = _.filter(comments, comment => {
        return comment.node.id !== comment_id;
      });
    }
    const commentUsersNode = {
      ...nodeDetails[commentsKey],
      edges: updatedComments,
      totalCount:
        itemType === "REPLY"
          ? nodeDetails[commentsKey].totalCount
          : nodeDetails[commentsKey].totalCount - 1,
    };

    writeCommentNodeFragment({
      nodeId,
      parentType,
      type,
      data: { ...nodeDetails, [commentsKey]: commentUsersNode },
      commentFilters,
      isCommunity,
    });
    try {
      await client.mutate({
        mutation: deleteMessageMutation,
        variables: {
          id: comment_id,
        },
      });

      if (isCommunity)
        recordEvent({
          eventName:
            itemType === "REPLY" ? "Comment reply deleted" : "Comment deleted",
          eventData: {
            source: "community",
            entity_id: nodeId,
            entity_type: type,
            entity_name: entityName,
          },
        });
    } catch (e) {
      writeCommentNodeFragment({
        nodeId,
        parentType,
        type,
        data: nodeDetails,
        commentFilters,
        isCommunity,
      });
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      throw e;
    }
  };
};
